import { startTransition, useCallback, useRef, useState, type ReactElement } from 'react'

import type { Poem } from '@maria-cavalheiro-website/shared-types'

import { EbookLauncher } from '../../ebook/ebook-launcher.tsx'
import {
  DEFAULT_GROUPING_MODE,
  groupingAnnouncement,
  poemGroups,
  type GroupingMode,
} from '../../poems/poem-groups.ts'
import { PoemIndex } from '../../poems/poem-index.tsx'
import { ShareSection } from '../../share/share-section.tsx'
import { Sky } from '../../sky/sky.tsx'
import { useSky } from '../../sky/use-sky.ts'
import type { ResolvedTheme } from '../../theme/use-theme.ts'
import { HeroSection } from './hero-section.tsx'

export interface HomePageProps {
  readonly poems: readonly Poem[]
  /** Owned by `app.tsx`; the sky repaints when it changes. */
  readonly theme?: ResolvedTheme
}

const HERO_HEADING_ID = 'titulo-inicio'
const POEMS_HEADING_ID = 'titulo-poemas'
const SHARE_HEADING_ID = 'titulo-social'

/** Names what happens, not the mechanism — "tela cheia" is the mechanism. */
const EBOOK_LABEL = 'Ler como livro'

/**
 * The home document: three sections, one `h1`.
 *
 * Container role only — it owns the grouping mode and nothing else. The groups
 * themselves come from a frozen module-level lookup, so there is no `useMemo`
 * here: adding one would imply the grouping is recomputed, which it is not.
 *
 * `#sobre` WAS THE FOURTH, and is now the `/sobre` route. The biography is about
 * a different subject from the collection this document indexes, and a route is
 * what gives it a canonical URL, its own title and its own `h1` — see
 * `features/pages/about/about-page.tsx`. The nav still says "Sobre mim" in the
 * same position, so nothing a reader knows about the site changed.
 *
 * Heading outline, fixed here and not negotiable downstream:
 *   h1  Poemas das coisas simples…      (#inicio)
 *   h2  O que vejo da janela            (#poemas)
 *     h3  <group label>
 *       h4  <poem title>
 *   h2  Se um poema te lembrou…         (#social)
 */
export function HomePage({ poems, theme = 'claro' }: HomePageProps): ReactElement {
  const [mode, setMode] = useState<GroupingMode>(DEFAULT_GROUPING_MODE)
  const [announcement, setAnnouncement] = useState('')

  // The sky repaints on theme change as well as on the tick. The theme arrives as
  // a prop rather than being observed from inside the effect — a MutationObserver
  // watching for your own write is a smell, and a second `useTheme` instance would
  // never learn that the header's had flipped the attribute.
  const skyRef = useRef<HTMLDivElement>(null)
  const sky = useSky(skyRef, theme)

  const handleModeChange = useCallback((next: GroupingMode) => {
    // Consistent with `use-reading-scale`: regrouping 23 cards is cheap, but the
    // INP budget is 200ms and matching the existing pattern costs nothing.
    // Switching must not move focus and must not scroll — neither happens here,
    // because nothing is imperatively focused or scrolled.
    startTransition(() => {
      setMode(next)
      setAnnouncement(groupingAnnouncement(next))
    })
  }, [])

  return (
    <div className="home">
      <section className="home__section home__section--hero" id="inicio" aria-labelledby={HERO_HEADING_ID}>
        <Sky ref={skyRef} />
        <HeroSection
          headingId={HERO_HEADING_ID}
          greeting={sky.greeting}
          clock={sky.clock}
          clockDateTime={sky.clockDateTime}
        />
      </section>

      <section className="home__section" id="poemas" aria-labelledby={POEMS_HEADING_ID}>
        <PoemIndex
          groups={poemGroups(mode)}
          mode={mode}
          onModeChange={handleModeChange}
          announcement={announcement}
          totalCount={poems.length}
          headingId={POEMS_HEADING_ID}
          // No `startSlug`: from the index the book opens at page one.
          action={<EbookLauncher poems={poems} label={EBOOK_LABEL} />}
        />
      </section>

      <section className="home__section" id="social" aria-labelledby={SHARE_HEADING_ID}>
        <ShareSection headingId={SHARE_HEADING_ID} />
      </section>
    </div>
  )
}
